/**
 * Drains every remaining page of an offset-paginated Apollo query into the
 * watched result, so a section can hold the full list rather than paging on
 * demand. Used by the Pending/Stalled transfer split (which must hold all
 * pending adoptions to partition them by age) and the Unassigned device
 * inventory section.
 *
 * The caller owns the `useQuery` and passes in the scalar page state plus a
 * `mergeItems` that appends a freshly-fetched page onto the accumulated result.
 * This hook owns the pagination bookkeeping:
 *
 *   - `inputRef` mirrors the active filter so `updateQuery` can compare the
 *     in-flight request's input against the now-current one and drop pages that
 *     arrived after the filter changed (otherwise Apollo would concatenate
 *     items from the old query into the new query's cache).
 *   - `paginatingRef` is a single-flight gate so we never have two `fetchMore`
 *     calls in flight for the same filter (relevant under StrictMode's
 *     double-invoke and back-to-back effect re-runs).
 *
 * Both refs reset whenever `input` changes so a new filter starts fresh.
 *
 * The `itemsLength >= totalCount` guard prevents an infinite fetch loop if the
 * server ever reports `hasMore=true` without returning new items.
 *
 * `input` and `mergeItems` MUST be referentially stable across renders for a
 * given filter (memoize them in the caller) — they are effect dependencies.
 */

import { useEffect, useRef } from 'react';

interface UseDrainAllPagesOptions<TData> {
  /** The `input` variable of the base query; also its identity for the filter. */
  input: Record<string, unknown>;
  hasMore: boolean;
  itemsLength: number;
  totalCount: number;
  /** The `fetchMore` returned by the caller's `useQuery`. */
  fetchMore: (options: {
    variables: { input: Record<string, unknown> };
    updateQuery: (previous: TData, options: { fetchMoreResult?: TData }) => TData;
  }) => Promise<unknown>;
  /** Append a freshly-fetched page's items onto the accumulated result. */
  mergeItems: (previous: TData, fetchMoreResult: TData) => TData;
}

export default function useDrainAllPages<TData>({
  input,
  hasMore,
  itemsLength,
  totalCount,
  fetchMore,
  mergeItems,
}: UseDrainAllPagesOptions<TData>): void {
  const inputRef = useRef(input);
  const paginatingRef = useRef(false);
  useEffect(() => {
    inputRef.current = input;
    paginatingRef.current = false;
  }, [input]);

  useEffect(() => {
    if (paginatingRef.current) {
      return;
    }
    if (!hasMore || itemsLength >= totalCount) {
      return;
    }
    const issuedInput = input;
    // Release the gate when the in-flight request settles (or fails to launch),
    // but only if the filter is still ours; the input-change effect already
    // handled the gate for any new filter.
    function releaseGate() {
      if (issuedInput === inputRef.current) {
        paginatingRef.current = false;
      }
    }
    paginatingRef.current = true;
    let pending: Promise<unknown>;
    try {
      pending = fetchMore({
        variables: { input: { ...issuedInput, offset: itemsLength } },
        updateQuery: (previous, { fetchMoreResult }) => {
          // Drop the result if the active filter changed mid-flight — otherwise
          // we'd merge old-filter items into the new filter's cache.
          if (issuedInput !== inputRef.current) {
            return previous;
          }
          if (!fetchMoreResult) {
            return previous;
          }
          return mergeItems(previous, fetchMoreResult);
        },
      });
    } catch {
      // Apollo doesn't normally throw synchronously, but if it ever does we
      // must still release the gate or the loop wedges for the rest of the
      // session under this filter.
      releaseGate();
      return;
    }
    pending.finally(releaseGate);
  }, [hasMore, itemsLength, totalCount, input, fetchMore, mergeItems]);
}
